import { ConditionalComponent } from '@/components/ConditionalComponent'
import { DefaultErrorBoundary } from '@/components/errors/DefaultErrorBoundary/DefaultErrorBoundary'
import { Header } from '@/components/layouts/Header'
import { Sidebar, type MenuItemProps } from '@/components/layouts/Sidebar'
import { QuickNavigation, type QuickNavigationOpenSource } from '@/components/QuickNavigation'
import { DashboardExperiments } from '@/experiments'
import { useSelfServeAndPermissions } from '@/hooks/permissions/useSelfServeAndPermissions'
import { useFlag } from '@/hooks/useFlag'
import { useFullLayout } from '@/hooks/useFullLayout'
import { PATHS } from '@/routes/PATHS'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { useCorePermissions } from '@/stores/permissions/permissions'
import { usePremiumFeaturesPermissions } from '@/stores/permissions/premiumFeatures'
import { OVERLAY_ID } from '@/utils/constants/elements'
import {
  AppstoreAddOutlined,
  AppstoreOutlined,
  ContainerOutlined,
  FormOutlined,
  ShopOutlined,
  SyncOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { Layout } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router'
import { useAnalyticsSidebarItems } from './hooks/useAnalyticsSidebarItems'
import { useReviewsSidebarItems } from './hooks/useReviewsSidebarItems'
import { useSettingsSidebarItems } from './hooks/useSettingsSidebarItems'
import styles from './styles.module.scss'

export const PAGE_LAYOUT_TEST_ID = 'page-layout-test-id'
export const PAGE_LAYOUT_CONTENT_ID = 'page-layout-content-id'
const SIDEBAR_WIDTH = 200

export function PageLayout() {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(false)
  const { pathname } = useLocation()
  const layoutContentRef = useRef<HTMLDivElement | null>(null)
  const isFullLayout = useFullLayout()
  const { shouldShowBillingAndPayments } = useSelfServeAndPermissions()

  const hideMenusForLocationManagersFlag = useFlag(
    DashboardExperiments.hideMenusForLocationManagers,
  )

  const listingsManagementPermission = useCorePermissions(CoreLevelFeatureNames.listingsManagement)
  const userManagementPermission = useCorePermissions(CoreLevelFeatureNames.userManagement)
  const menuManagementPermission = useCorePermissions(CoreLevelFeatureNames.menuManagement)
  const socialManagementPermission = useCorePermissions(CoreLevelFeatureNames.socialManagementPosts)

  const premiumFeaturesPermissions = usePremiumFeaturesPermissions()
  const hasReviewMonitoringFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewMonitoring,
  )
  const hasMenusFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Menus)
  const hasListingsFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Listings)
  const hasSocialManagementFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.SocialManagement,
  )
  const hasWidgetMenusFeature =
    premiumFeaturesPermissions.includes(PremiumFeatures.WidgetMenus) && hasMenusFeature
  const hasWidgetHoursFeature = premiumFeaturesPermissions.includes(PremiumFeatures.WidgetHours)

  const hasUserManagementPermission = useMemo(() => {
    return !!userManagementPermission?.edit
  }, [userManagementPermission])

  const hasMenuManagementPermission = useMemo(() => {
    return !!menuManagementPermission?.edit
  }, [menuManagementPermission])

  const shouldHideMenuPageItem = useMemo(() => {
    return !!menuManagementPermission?.hide
  }, [menuManagementPermission])

  const shouldHideListingsPageItem = useMemo(() => {
    return !!listingsManagementPermission?.hide
  }, [listingsManagementPermission])

  const hasSocialManagementPermission = useMemo(() => {
    return !!socialManagementPermission?.view
  }, [socialManagementPermission])

  const hasHoursWidgetsManagementPermission = useMemo(() => {
    return !shouldHideListingsPageItem
  }, [shouldHideListingsPageItem])

  const hasMenusWidgetsManagementPermission = useMemo(() => {
    return !shouldHideMenuPageItem
  }, [shouldHideMenuPageItem])

  const isLocationManager = hasMenuManagementPermission && !hasUserManagementPermission

  const shouldHideMenusForLocationManagers = hideMenusForLocationManagersFlag && isLocationManager

  const hideMenuPageItem = !hasMenusFeature || shouldHideMenuPageItem

  const hideListingsPageItem = shouldHideListingsPageItem || !hasListingsFeature

  const shouldShowSocialManagementButton =
    hasSocialManagementFeature && hasSocialManagementPermission

  const shouldShowWidgets =
    (hasWidgetMenusFeature && hasMenusWidgetsManagementPermission) ||
    (hasWidgetHoursFeature && hasHoursWidgetsManagementPermission)

  const availableReviewsItems = useReviewsSidebarItems()
  const availableAnalyticsItems = useAnalyticsSidebarItems()
  const availableSettingsItems = useSettingsSidebarItems()

  const allSidebarItems = [
    {
      icon: <AppstoreOutlined />,
      key: PATHS.homePage,
      label: t('sidebarItems.home'),
    },
    {
      icon: <ShopOutlined />,
      key: PATHS.locationsPage,
      label: t('sidebarItems.locations'),
    },
    {
      icon: <SyncOutlined />,
      key: PATHS.listingsPage,
      label: t('sidebarItems.listings.text'),
    },
    {
      icon: <ContainerOutlined />,
      key: PATHS.menusPage,
      label: t('sidebarItems.menus'),
    },
    {
      icon: <FormOutlined />,
      key: PATHS.publishedTab,
      label: t('sidebarItems.socialManagement'),
    },
    {
      icon: <AppstoreAddOutlined />,
      key: PATHS.widgetsList,
      label: t('sidebarItems.widgets'),
    },
    {
      key: 'reviewsGroup',
      label: collapsed ? '' : t('sidebarItems.reviews'),
      children: availableReviewsItems,
      type: 'group',
    },
    {
      key: 'analyticsGroup',
      label: collapsed ? '' : t('sidebarItems.analytics'),
      children: availableAnalyticsItems,
      type: 'group',
    },
    {
      key: 'settingsGroup',
      label: collapsed ? '' : t('sidebarItems.settings'),
      children: availableSettingsItems,
      type: 'group',
    },
  ]

  const availableSidebarItems = allSidebarItems.filter((item) =>
    hasReviewMonitoringFeature ? true : item?.key !== 'reviewsGroup',
  )

  const filteredSidebarItems = availableSidebarItems
    .filter((item) => (shouldShowSocialManagementButton ? true : item?.key !== PATHS.publishedTab))
    .filter((item) => (shouldShowWidgets ? true : item?.key !== PATHS.widgetsList))
    .filter((item) => (hideMenuPageItem ? item?.key !== PATHS.menusPage : true))
    .filter((item) => (shouldHideMenusForLocationManagers ? item?.key !== PATHS.menusPage : true))
    .filter((item) => (hideListingsPageItem ? item?.key !== PATHS.listingsPage : true))
    .filter((item) => item.type !== 'group' || (item.children?.length ?? 0) > 0)

  const extraLinks: React.ComponentProps<typeof QuickNavigation>['navigationItems'] =
    shouldShowBillingAndPayments
      ? [
          {
            key: PATHS.userProfilePage,
            icon: <UserOutlined />,
            label: t('components.navbar.userProfile'),
          },
          {
            key: PATHS.billingAndPaymentsPage,
            icon: <WalletOutlined />,
            label: t('components.navbar.billingAndPayments'),
          },
        ]
      : [
          {
            key: PATHS.userProfilePage,
            icon: <UserOutlined />,
            label: t('components.navbar.userProfile'),
          },
        ]

  const quickNavigationItems = [
    ...filteredSidebarItems.reduce<React.ComponentProps<typeof QuickNavigation>['navigationItems']>(
      (acc, item) => {
        if ('type' in item && item.type === 'group') {
          acc.push(...item.children)
        } else {
          acc.push(item)
        }
        return acc
      },
      [],
    ),
    ...extraLinks,
  ]

  const sidebarItems = filteredSidebarItems.map((item) => {
    if ('type' in item && item.type === 'group') {
      return {
        ...item,
        children: item.children.map((child) => ({
          ...child,
          label: <Link to={child.key}>{child.label}</Link>,
        })),
      }
    }
    return {
      ...item,
      label: <Link to={item.key}>{item.label}</Link>,
    }
  })

  useEffect(() => {
    if (layoutContentRef.current) {
      layoutContentRef.current.scrollTop = 0
    }
  }, [pathname])

  const handleQuickNavigationOpen = (source: QuickNavigationOpenSource) => {
    setQuickFinderOpen(source)
  }

  const [quickFinderOpen, setQuickFinderOpen] = useState<QuickNavigationOpenSource | null>(null)

  const hasQuickNavigationFeature = useFlag(DashboardExperiments.quickNavigation)

  useHotkeys(
    'Meta+k',
    () => {
      if (hasQuickNavigationFeature) {
        handleQuickNavigationOpen('keyboard')
      }
    },
    [hasQuickNavigationFeature],
  )

  return (
    <Layout className={styles.pageLayout} data-testid={PAGE_LAYOUT_TEST_ID}>
      <Header
        onMenuClick={() => setCollapsed((prevValue) => !prevValue)}
        onQuickNavigationClick={() => handleQuickNavigationOpen('button')}
      />

      <Layout>
        <ConditionalComponent condition={isFullLayout}>
          <Sidebar
            width={SIDEBAR_WIDTH}
            menuItems={sidebarItems as MenuItemProps[]}
            defaultOpenKeys={['reviews', PATHS.analyticsPage]}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        </ConditionalComponent>
        <Layout.Content
          className={styles.pageContent}
          id={PAGE_LAYOUT_CONTENT_ID}
          ref={layoutContentRef}
        >
          <DefaultErrorBoundary context="APP" fallbackUrl={PATHS.homePage}>
            <Outlet />
          </DefaultErrorBoundary>
        </Layout.Content>
      </Layout>

      {hasQuickNavigationFeature && (
        <QuickNavigation
          navigationItems={quickNavigationItems}
          open={quickFinderOpen !== null}
          setOpen={setQuickFinderOpen}
          source={quickFinderOpen}
        />
      )}
      <div id={OVERLAY_ID} />
    </Layout>
  )
}
